import { useState, useCallback, useEffect } from 'react';
import { api, BuilderTemplate } from '@/lib/api';

interface UseBuilderTemplatesOptions {
  category?: string;
  featured?: boolean;
  autoFetch?: boolean;
}

interface UseBuilderTemplatesReturn {
  templates: BuilderTemplate[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useBuilderTemplates({
  category,
  featured,
  autoFetch = true,
}: UseBuilderTemplatesOptions = {}): UseBuilderTemplatesReturn {
  const [templates, setTemplates] = useState<BuilderTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.listBuilderTemplates({ category, featured });
      setTemplates(response.templates);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch templates');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [category, featured]);

  useEffect(() => {
    if (autoFetch) {
      fetchTemplates();
    }
  }, [autoFetch, fetchTemplates]);

  return {
    templates,
    isLoading,
    error,
    refresh: fetchTemplates,
  };
}

interface UseBuilderTemplateOptions {
  templateId: string;
}

interface UseBuilderTemplateReturn {
  template: BuilderTemplate | null;
  isLoading: boolean;
  error: Error | null;
}

export function useBuilderTemplate({
  templateId,
}: UseBuilderTemplateOptions): UseBuilderTemplateReturn {
  const [template, setTemplate] = useState<BuilderTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.getBuilderTemplate(templateId);
        setTemplate(response.template);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch template');
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplate();
  }, [templateId]);

  return {
    template,
    isLoading,
    error,
  };
}
